import React, { useContext, useEffect, useState } from 'react'
import Row from 'react-bootstrap/Row'
import Col from 'react-bootstrap/Col'
import Container from 'react-bootstrap/Container'
import Button from 'react-bootstrap/Button'
import { ViewSampleNav } from './components'
import './ViewSampleContainer.scss'
import { useTranslation } from 'react-i18next'
import { generatePath, useLocation, useNavigate, useParams } from 'react-router-dom'
import { appRoutes } from '../../utils/routes'
import { trackPromise } from 'react-promise-tracker'
import { Sample } from '../../models/Sample'
import { AxiosResponse } from 'axios'
import { AppUtil } from '../../utils/app.utils'
import { AuthUtil } from '../../utils/auth.utils'
import { getSampleDetails } from '../../services/Sample'
import { HTTP_STATUS_CODE, PRIVILEGES, MASTER_DATA_KEYS as MDK } from '../../utils/constants'
import { PersonSquare, PencilFill, ArrowLeftShort } from 'react-bootstrap-icons'
import { Patient } from '../../models/RegisterPatient'
import { getPatientDetails } from '../../services'
import { MasterDataContext } from '../../contexts'
import { formatCategoryData, formatSubCategoryData, formatSubCategoryFieldsData } from '../SampleInformationForm/helpers/AddSampleInfoHelpers'
import { WarningModal } from '../PatientList/WarningModal'


const translatn = 'viewSample.'
const parentClass = 'vcr-pp-view-sample-'

export const ViewSampleContainer = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const { patientId, id } = useParams()
  const { masterData } = useContext(MasterDataContext)

  const [isEditAllowed, setIsEditAllowed] = useState(false)
  const [showFormModal, setShowFormModal] = useState(false)
  const [sampleDetails, setSampleDetails] = useState<Sample | null>(null)
  const [patientDetails, setPatientDetails] = useState<Patient | null>(null)

  const hasEditSamplePrivilege = AuthUtil.checkUserAccess(PRIVILEGES.EDIT_SAMPLE)
  const hasEditBeforeShippmentPrivilege = AuthUtil.checkUserAccess(PRIVILEGES.EDIT_BEFORE_SHIPMENT)
  const hasEditAfterShippmentPrivilege = AuthUtil.checkUserAccess(PRIVILEGES.EDIT_AFTER_SHIPMENT)

  const checkPrivilege = (data: Sample) => {
    return hasEditSamplePrivilege && (
      hasEditAfterShippmentPrivilege && [2, 3].includes(data?.sampleStatus ?? -1) ||
      hasEditAfterShippmentPrivilege && [5].includes(data?.sampleStatus ?? -1) && [2, 3].includes(data?.associatedWorkFlowStatus ?? -1) ||
      hasEditBeforeShippmentPrivilege && [0, 1].includes(data?.sampleStatus ?? -1) ||
      hasEditBeforeShippmentPrivilege && [5].includes(data?.sampleStatus ?? -1) && [0, 1].includes(data?.associatedWorkFlowStatus ?? -1)
    )
  }

  useEffect(() => {
    if (id) {
      trackPromise(
        getSampleDetails(id, patientId ?? '').then(
          (res: AxiosResponse<Sample>) => {
            setSampleDetails({
              ...res?.data, biomarkerTestCategory: formatCategoryData(res?.data?.sampleMarkerDetails ?? [], masterData, MDK.BIOMARKER_TEST), biomarkerTestSubCategory: formatSubCategoryData(res?.data?.sampleMarkerDetails ?? [], masterData, MDK.BIOMARKER_TEST), biomarkerTestSubCategoryFields: formatSubCategoryFieldsData(res?.data?.sampleMarkerDetails, masterData, MDK.BIOMARKER_TEST), labTypeCategory: formatCategoryData(res?.data?.sampleMarkerDetails ?? [], masterData, MDK.LAB_TYPE),
              labTypeSubCategory: formatSubCategoryData(res?.data?.sampleMarkerDetails ?? [], masterData, MDK.LAB_TYPE),
              labTypeSubCategoryFields: formatSubCategoryFieldsData(res?.data?.sampleMarkerDetails ?? [], masterData, MDK.LAB_TYPE),
              outcomesOfTreatmentCategory: formatCategoryData(res?.data?.sampleMarkerDetails ?? [], masterData, MDK.PROGRESSION_SOURCE),
              outcomesOfTreatmentSubCategory: formatSubCategoryData(res?.data?.sampleMarkerDetails ?? [], masterData, MDK.PROGRESSION_SOURCE),
              outcomesOfTreatmentSubCategoryFields: formatSubCategoryFieldsData(res?.data?.sampleMarkerDetails ?? [], masterData, MDK.PROGRESSION_SOURCE),
            })
            if (checkPrivilege(res?.data)) {
              setIsEditAllowed(true)
            }
          },
          (error) => {
            AppUtil.logError(error)
            if (error.response.status === HTTP_STATUS_CODE.NOT_FOUND) {
              setTimeout(() => {
                navigate(appRoutes.home)
              }, 2000)
            }
          },
        ),
      )
    }
  }, [id, masterData])

  useEffect(() => {
    if (patientId) {
      trackPromise(
        getPatientDetails(patientId).then(
          (res: AxiosResponse<Patient>) => {
            setPatientDetails(res.data)
          },
          (error) => AppUtil.logError(error),
        ),
      )
    }
  }, [patientId])

  const handleFormModalClose = () => {
    setShowFormModal(false)
  }

  const handleModal = () => {
    const requiredFields = {
      addressLine1: patientDetails?.addressLine1,
      nationality: patientDetails?.nationality,
      consentProvided: patientDetails?.consentProvided,
      ethnicity: patientDetails?.ethnicity,
      race: patientDetails?.race,
      state: patientDetails?.state,
      city: patientDetails?.city,
      pinCode: patientDetails?.pinCode,
      uhid: patientDetails?.uhid,
      diagnosis: patientDetails?.diagnosis,
      cancerType: patientDetails?.cancerType,
    }

    if (Object.values(requiredFields).some((val) => val === null || val === '')) {
      setShowFormModal(true)
    } else {
      setShowFormModal(false)
      navigate(
        generatePath(appRoutes.editSample, {
          patientId,
          id,
        }),
        { state: sampleDetails },
      )
    }
  }

  const handleEdit = () => {
    navigate(generatePath(appRoutes.editPatient, { id: patientId }), {
      state: { ...patientDetails },
    })
  }

  return (
    <Container className={`${parentClass}container`} fluid>
      <WarningModal open={showFormModal} handleClose={handleFormModalClose} handleEdit={handleEdit} />
      <Row className={`${parentClass}header`}>
        <Col className="d-flex align-items-center justify-content-start">
          <ArrowLeftShort size={26} onClick={() => location.state ? navigate(-1) : navigate(generatePath(appRoutes.viewPatientDetails, { id: patientId }) + "#samples")} />
          <span className={`${parentClass}vertical-line`}></span>
          <PersonSquare color="#1C9AA3" size={26} className="mr-3 ml-3" />
          <span className={`${parentClass}profile-id`}>
            {[
              AppUtil.combineNamesString(patientDetails?.name, patientDetails?.firstName, patientDetails?.middleName, patientDetails?.lastName),
              (AppUtil.getLabel(MDK.GENDER, masterData, patientDetails?.gender) + (patientDetails?.genderOther ? ` - ${patientDetails?.genderOther}` : '')),
              patientDetails?.age
            ].join(', ')}
            {' (' + t(`${translatn}patientId`) + ' ' + patientId + ') - Sample ID: ' + id}
          </span>
        </Col>
        <Col className="d-flex align-items-center justify-content-end">
          {isEditAllowed && (
            <Button
              className={`${parentClass}editbtn`}
              onClick={() => handleModal()}
            >
              <PencilFill color="#075883" />
              <span className="ps-2">{t(`${translatn}editInfo`)}</span>
            </Button>
          )}
        </Col>
      </Row>
      <Row>
        <Col>
          <ViewSampleNav sampleDetails={sampleDetails} />
        </Col>
      </Row>
    </Container>
  )
}
